import { useEffect } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import DOMPurify from "dompurify";
import ReactMarkdown from "react-markdown";
import { Bold, Italic, List, ListOrdered, Strikethrough } from "lucide-react";

const looksLikeHtml = (s) => typeof s === "string" && /<\/?[a-z][\s\S]*>/i.test(s);

const isEmptyHtml = (html) => {
  if (!html) return true;
  const stripped = html.replace(/<[^>]*>/g, "").replace(/&nbsp;/g, "").trim();
  return stripped.length === 0;
};

function ToolbarButton({ active, onClick, label, children, testid }) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      data-testid={testid}
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClick}
      className={`h-7 w-7 inline-flex items-center justify-center rounded transition-colors ${
        active ? "bg-primary/10 text-primary" : "text-muted-foreground hover:bg-accent"
      }`}
    >
      {children}
    </button>
  );
}

export function RichTextEditor({ value, onChange, placeholder = "Write something…", testid = "rich-editor" }) {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: false,
      }),
    ],
    content: value || "",
    editorProps: {
      attributes: {
        class:
          "prose prose-sm max-w-none focus:outline-none min-h-[80px] px-3 py-2 [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5",
        "data-testid": `${testid}-content`,
      },
    },
    onUpdate: ({ editor }) => {
      const html = editor.getHTML();
      onChange(isEmptyHtml(html) ? "" : html);
    },
  });

  // Keep external value in sync (e.g. reset after submit)
  useEffect(() => {
    if (!editor) return;
    const current = editor.getHTML();
    const next = value || "";
    if ((value === "" || value == null) && !isEmptyHtml(current)) {
      editor.commands.clearContent();
    } else if (next && next !== current && !editor.isFocused) {
      editor.commands.setContent(next);
    }
  }, [value, editor]);

  if (!editor) return null;

  const empty = isEmptyHtml(editor.getHTML());

  return (
    <div className="rounded-md border border-input bg-background" data-testid={testid}>
      <div className="flex items-center gap-0.5 border-b border-border px-1.5 py-1">
        <ToolbarButton
          label="Bold"
          testid={`${testid}-bold`}
          active={editor.isActive("bold")}
          onClick={() => editor.chain().focus().toggleBold().run()}
        >
          <Bold className="h-3.5 w-3.5" strokeWidth={2.25} />
        </ToolbarButton>
        <ToolbarButton
          label="Italic"
          testid={`${testid}-italic`}
          active={editor.isActive("italic")}
          onClick={() => editor.chain().focus().toggleItalic().run()}
        >
          <Italic className="h-3.5 w-3.5" strokeWidth={2.25} />
        </ToolbarButton>
        <ToolbarButton
          label="Strikethrough"
          testid={`${testid}-strike`}
          active={editor.isActive("strike")}
          onClick={() => editor.chain().focus().toggleStrike().run()}
        >
          <Strikethrough className="h-3.5 w-3.5" strokeWidth={2.25} />
        </ToolbarButton>
        <span className="mx-1 h-4 w-px bg-border" />
        <ToolbarButton
          label="Bullet list"
          testid={`${testid}-bullet`}
          active={editor.isActive("bulletList")}
          onClick={() => editor.chain().focus().toggleBulletList().run()}
        >
          <List className="h-3.5 w-3.5" strokeWidth={2.25} />
        </ToolbarButton>
        <ToolbarButton
          label="Numbered list"
          testid={`${testid}-ordered`}
          active={editor.isActive("orderedList")}
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
        >
          <ListOrdered className="h-3.5 w-3.5" strokeWidth={2.25} />
        </ToolbarButton>
      </div>
      <div className="relative">
        {empty && (
          <div className="pointer-events-none absolute left-3 top-2 text-sm text-muted-foreground">
            {placeholder}
          </div>
        )}
        <EditorContent editor={editor} />
      </div>
    </div>
  );
}

export function RichTextView({ content, className = "" }) {
  if (!content) return null;
  if (looksLikeHtml(content)) {
    const clean = DOMPurify.sanitize(content, {
      ALLOWED_TAGS: ["p", "br", "strong", "b", "em", "i", "s", "del", "ul", "ol", "li", "a", "code", "blockquote"],
      ALLOWED_ATTR: ["href", "target", "rel"],
    });
    return (
      <div
        className={`text-sm prose prose-sm max-w-none [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5 ${className}`}
        dangerouslySetInnerHTML={{ __html: clean }}
      />
    );
  }
  return (
    <div className={`text-sm prose prose-sm max-w-none ${className}`}>
      <ReactMarkdown>{content}</ReactMarkdown>
    </div>
  );
}
